import { useMemo } from 'react'
import DatePicker, {
  ReactDatePickerProps,
  registerLocale,
} from 'react-datepicker'
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next'
import ja from 'date-fns/locale/ja'
import en from 'date-fns/locale/en-US'
import ko from 'date-fns/locale/ko'
import { getDate } from 'date-fns'
import { getStringFullDate } from 'config/helpers'
import { useCalendarHolidays } from 'hooks'
import { HolidayDateType } from 'hooks/useCalendarHolidays'
import CalendarDateToolTip from '../Calendar/DateTooltip'

registerLocale('ja', ja)
registerLocale('en', en)
registerLocale('ko', ko)

const DatePickerCustom = (props: ReactDatePickerProps): JSX.Element => {
  const { language } = useI18next()
  const { t } = useTranslation()
  const { holidaysDataSource, publicHolidays, excludeDates } =
    useCalendarHolidays()

  const stringPublichHolidays = useMemo(() => {
    return publicHolidays?.map((d: Date) => getStringFullDate(d)) || []
  }, [publicHolidays])

  const stringClosedDates = useMemo(() => {
    return excludeDates?.map((d: Date) => getStringFullDate(d)) || []
  }, [excludeDates])

  // 各日付表示内容のレンダー関数
  // 予約できる日付：白い数字
  // 休業日(isClose): 赤い「X」のiconで表示、tooltipで提示
  // 祝日(isPublic): 赤い数字、tooltipで祝日名を提示
  const renderDayContents = (day: number, date: Date): JSX.Element => {
    const pickerStringDate = getStringFullDate(date)
    const isClose = stringClosedDates?.includes(pickerStringDate)
    const isPublic = stringPublichHolidays?.includes(pickerStringDate)

    let publicHolidayName = ''
    holidaysDataSource?.forEach((d: HolidayDateType) => {
      if (d?.date === pickerStringDate) publicHolidayName = d?.reason
    })

    let content: JSX.Element = (
      <span className="datepicker-date-content-text">
        {getDate(date).toString()}
      </span>
    )
    if (isClose) content = getCostomDateContentIsClose()
    else if (isPublic)
      content = getCostomDateContentIsPublic(
        getDate(date).toString(),
        publicHolidayName
      )

    return <span className="datepicker-date-content">{content}</span>
  }

  // 休業日など予約不可の日付(isClose)の表示内容
  const getCostomDateContentIsClose = () => {
    return (
      <CalendarDateToolTip
        isIconContent
        iconType={'isClose'}
        tooltipContent={t(
          'components.datepicker.date-picker-calendar-date-tooltip-isclose'
        )}
        className="datepicker-tooltip"
        tooltipClassName="datepicker-tooltip-content"
        textContentClassName="datepicker-date-content-text"
      />
    )
  }

  // 祝日(isPublic)の表示内容
  const getCostomDateContentIsPublic = (
    date: string,
    publicHolidayName: string
  ) => {
    return (
      <CalendarDateToolTip
        isTextContent
        textContent={date}
        tooltipContent={publicHolidayName}
        className="datepicker-tooltip"
        tooltipClassName="datepicker-tooltip-content"
        textContentClassName="datepicker-date-content-text"
      />
    )
  }

  // 祝日(isPublic)に赤色の ClassName を付ける
  const handleHolidaysClassName = (date: Date): string | undefined => {
    const pickerStringDate = getStringFullDate(date)
    const flag = stringPublichHolidays?.includes(pickerStringDate)
    return flag ? 'random' : undefined
  }

  return (
    <DatePicker
      {...props}
      locale={!language || language === 'jp' ? 'ja' : language}
      dateFormat={!language || language === 'jp' ? 'yyyy/MM/dd' : 'dd/MM/yyyy'}
      showPopperArrow={false}
      // placementRight があればイコール CSS right: 0
      // hasEndDateLimitation があれば範囲 Range の endate 以降の日付(disabled)は #999 になる
      popperClassName={
        props.popperClassName +
        (props.placementRight ? 'canlendar-placement-right' : '') +
        (props.hasEndDateLimitation ? 'canlendar-range-end-date' : '')
      }
      // 休業日など選択できない日付(isClose)
      excludeDates={excludeDates}
      // 祝日(isPublic)
      dayClassName={(date: Date) => handleHolidaysClassName(date)}
      // 各日付表示内容のレンダー関数
      renderDayContents={renderDayContents}
    >
      {/* カレンダー内部の下の提示メッセージ */}
      {props.bottomText && (
        <div className="clander-bottom-text">{props.bottomText}</div>
      )}
    </DatePicker>
  )
}
export default DatePickerCustom
